/** Shared design tokens — dark sidebar + indigo enterprise theme */

export const brand = {
  text: '#1e293b',
  textMuted: '#64748b',
  accent: '#5a67d8',
  accentDark: '#4c51bf',
  accentLight: '#7f8ce8',
  sky: '#3b82f6',
  mint: '#22c55e',
};

export const primaryGradient = 'linear-gradient(135deg, #5a67d8 0%, #6b76e0 50%, #7f8ce8 100%)';
export const sidebarGradient = 'linear-gradient(180deg, #161d2b 0%, #1a2332 55%, #1e293b 100%)';
export const sidebarAccent = '#5a67d8';
export const contentBg = 'radial-gradient(ellipse 90% 60% at 50% -10%, rgba(90, 103, 216, 0.07), transparent 70%), #f8fafc';

export const layout = {
  pageMaxWidth: 1440,
  detailMaxWidth: 800,
  sidebarWidth: 260,
  mobileBottomNavHeight: 64,
};

export const shadows = {
  card: '0 2px 12px -2px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.04)',
  cardHover: '0 8px 28px -6px rgba(90, 103, 216, 0.14)',
  modal: '0 24px 48px -12px rgba(15, 23, 42, 0.18)',
  header: '0 1px 0 0 rgba(15, 23, 42, 0.06)',
  glow: '0 4px 20px rgba(90, 103, 216, 0.45)',
  sidebar: '4px 0 24px rgba(0, 0, 0, 0.15)',
};

export const pageContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 2, sm: 2.5 },
  width: '100%',
  maxWidth: layout.pageMaxWidth,
  mx: 'auto',
  minWidth: 0,
};

export const cardSx = {
  borderRadius: 3.5,
  border: '1px solid',
  borderColor: 'grey.200',
  bgcolor: 'background.paper',
  boxShadow: shadows.card,
  overflow: 'hidden',
  transition: 'box-shadow 0.25s ease, transform 0.25s ease',
  '&:hover': {
    boxShadow: shadows.cardHover,
  },
};

export const statCardSx = (accent) => ({
  ...cardSx,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: accent,
  },
  '&:hover': {
    boxShadow: shadows.cardHover,
    transform: 'translateY(-2px)',
  },
});

export const tableHeaderSx = {
  py: 1.25,
  px: 2,
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'text.secondary',
  bgcolor: 'grey.50',
  borderBottom: '1px solid',
  borderColor: 'divider',
  whiteSpace: 'nowrap',
};

export const tableCellSx = {
  py: 1.5,
  px: 2,
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1.55,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

export const labelSx = {
  fontSize: '0.75rem',
  fontWeight: 500,
  letterSpacing: '0.02em',
  color: 'text.secondary',
  mb: 0.75,
  display: 'block',
};

export const inputFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    fontSize: '0.875rem',
    bgcolor: 'background.paper',
    transition: 'box-shadow 0.2s ease',
    '& fieldset': { borderColor: 'grey.200' },
    '&:hover fieldset': { borderColor: 'grey.300' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '1.5px' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(90, 103, 216, 0.15)' },
  },
};

export const filterFieldSx = {
  ...inputFieldSx,
  '& .MuiOutlinedInput-root': {
    ...inputFieldSx['& .MuiOutlinedInput-root'],
    height: 36,
    fontSize: '0.8125rem',
  },
};

export const modalPaperSx = {
  borderRadius: 3.5,
  boxShadow: shadows.modal,
  bgcolor: 'background.paper',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'divider',
};

export const dialogTitleSx = {
  fontSize: '1rem',
  fontWeight: 600,
  color: 'text.primary',
  px: 3,
  py: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  lineHeight: 1.4,
  background: 'linear-gradient(90deg, rgba(90,103,216,0.05) 0%, transparent 100%)',
};

export const primaryButtonSx = {
  height: 40,
  borderRadius: 999,
  px: 2.5,
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 600,
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  background: primaryGradient,
  transition: 'box-shadow 0.2s ease, transform 0.15s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #4c51bf 0%, #5a67d8 50%, #6b76e0 100%)',
    boxShadow: shadows.glow,
    transform: 'translateY(-1px)',
  },
};

export const iconButtonSx = {
  width: 32,
  height: 32,
  borderRadius: 2.5,
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  bgcolor: 'background.paper',
  transition: 'all 0.2s ease',
  '&:hover': { bgcolor: 'rgba(90, 103, 216, 0.06)', color: 'primary.main', borderColor: 'rgba(90, 103, 216, 0.35)' },
};

export const pageHeaderSx = {
  display: 'flex',
  alignItems: { xs: 'flex-start', sm: 'center' },
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 2,
  p: { xs: 2, sm: 2.75 },
  borderRadius: 3.5,
  background: 'linear-gradient(135deg, #ffffff 0%, #fafbff 60%, #f5f7ff 100%)',
  border: '1px solid',
  borderColor: 'rgba(90, 103, 216, 0.12)',
  boxShadow: shadows.card,
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: primaryGradient,
  },
};

export const segmentControlSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.25,
  p: 0.375,
  borderRadius: 3,
  bgcolor: 'grey.100',
  border: '1px solid',
  borderColor: 'grey.200',
  width: 'fit-content',
  flexWrap: 'wrap',
  boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.04)',
};

export const segmentButtonSx = (isActive) => ({
  textTransform: 'none',
  fontSize: '0.8125rem',
  fontWeight: isActive ? 600 : 500,
  minHeight: 34,
  px: 1.75,
  borderRadius: 2.5,
  color: isActive ? 'primary.main' : 'text.secondary',
  bgcolor: isActive ? 'background.paper' : 'transparent',
  boxShadow: isActive ? '0 2px 8px rgba(90, 103, 216, 0.15)' : 'none',
  border: '1px solid',
  borderColor: isActive ? 'rgba(90, 103, 216, 0.3)' : 'transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    bgcolor: isActive ? 'background.paper' : 'rgba(255,255,255,0.7)',
    color: isActive ? 'primary.dark' : 'text.primary',
  },
});

export const segmentCountSx = (isActive) => ({
  ml: 0.75,
  px: 0.75,
  py: 0.125,
  borderRadius: 999,
  fontSize: '10px',
  fontWeight: 700,
  fontFamily: 'monospace',
  bgcolor: isActive ? 'rgba(90, 103, 216, 0.14)' : 'rgba(15, 23, 42, 0.06)',
  color: isActive ? 'primary.dark' : 'text.disabled',
});

export const customerNameSx = {
  textTransform: 'none',
  fontWeight: 400,
  fontSize: '1rem',
  color: 'text.primary',
  p: 0,
  minWidth: 0,
  lineHeight: 1.45,
  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline', color: 'primary.main' },
};

export const metaTextSx = {
  fontSize: '0.9375rem',
  fontWeight: 400,
  color: 'text.secondary',
  lineHeight: 1.5,
};

export const metaMonoSx = {
  ...metaTextSx,
  fontFamily: 'monospace',
};

export const dateTextSx = {
  fontSize: '0.9375rem',
  fontFamily: 'monospace',
  fontWeight: 400,
  color: 'text.secondary',
  lineHeight: 1.5,
};

export const dateTodaySx = {
  fontWeight: 400,
  color: 'primary.main',
};

export const dateOverdueSx = {
  fontWeight: 600,
  color: '#dc2626',
};

export const statusStyles = {
  ORDERED: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', dot: '#f97316' },
  DISPATCHED: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', dot: '#3b82f6' },
  RECEIVED: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', dot: '#22c55e' },
  RETURNED: { bg: '#ecfdf5', color: '#059669', border: '#a7f3d0', dot: '#10b981' },
  DELIVERED: { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe', dot: '#8b5cf6' },
  CANCELLED: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', dot: '#ef4444' },
  CONVERTED: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', dot: '#22c55e' },
  PENDING: { bg: '#f8fafc', color: '#475569', border: '#e2e8f0', dot: '#94a3b8' },
  OVERDUE: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', dot: '#ef4444' },
};

export const chartColors = ['#5a67d8', '#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#4c51bf'];
